import React, { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import DepartmentSelector from "../selectors/DepartmentSelector";
import UserSelector from "../selectors/UserSelector";
import { Pen, Eraser } from "lucide-react";

export interface WhiteboardShareFormData {
  type: "USER" | "DEPARTMENT";
  selectedUsers: Array<{ id: string; user: any; canDraw: boolean; canErase: boolean }>;
  selectedDepartments: Array<{ id: string; department: any; canDraw: boolean; canErase: boolean }>;
}

interface WhiteboardShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: WhiteboardShareFormData) => void;
  isLoading: boolean;
  title?: string;
  excludeUserIds?: string[];
  excludeDepartmentIds?: string[];
  container?: HTMLElement | null;
}

export default function WhiteboardShareModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  title = "Tahtayı Paylaş",
  excludeUserIds = [],
  excludeDepartmentIds = [],
  container,
}: WhiteboardShareModalProps) {
  const [formData, setFormData] = useState<WhiteboardShareFormData>({
    type: "USER",
    selectedUsers: [],
    selectedDepartments: [],
  });

  const [currentSelectedUser, setCurrentSelectedUser] = useState<string>("");
  const [currentSelectedDepartment, setCurrentSelectedDepartment] = useState<string>("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const resetForm = () => {
    setFormData({
      type: "USER",
      selectedUsers: [],
      selectedDepartments: [],
    });
    setCurrentSelectedUser("");
    setCurrentSelectedDepartment("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    resetForm();
  }, [isOpen]);

  const handleTypeChange = (newType: "USER" | "DEPARTMENT") => {
    setFormData({
      type: newType,
      selectedUsers: [],
      selectedDepartments: [],
    });
    setCurrentSelectedUser("");
    setCurrentSelectedDepartment("");
  };

  const handleUserAdd = (userId: string, user: any) => {
    if (userId && user && !formData.selectedUsers.find((u) => u.id === userId)) {
      setFormData((prev) => ({
        ...prev,
        selectedUsers: [...prev.selectedUsers, { id: userId, user, canDraw: false, canErase: false }],
      }));
      setCurrentSelectedUser("");
    }
  };

  const handleUserRemove = (userId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.filter((u) => u.id !== userId),
    }));
  };

  const handleUserPermissionChange = (userId: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.map((u) =>
        u.id === userId ? { ...u, canDraw: value } : u
      ),
    }));
  };

  const handleUserErasePermissionChange = (userId: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.map((u) =>
        u.id === userId ? { ...u, canErase: value } : u
      ),
    }));
  };

  const handleDepartmentAdd = (departmentId: string, department: any) => {
    if (departmentId && department && !formData.selectedDepartments.find((d) => d.id === departmentId)) {
      setFormData((prev) => ({
        ...prev,
        selectedDepartments: [...prev.selectedDepartments, { id: departmentId, department, canDraw: false, canErase: false }],
      }));
      setCurrentSelectedDepartment("");
    }
  };

  const handleDepartmentRemove = (departmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedDepartments: prev.selectedDepartments.filter((d) => d.id !== departmentId),
    }));
  };

  const handleDepartmentPermissionChange = (departmentId: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      selectedDepartments: prev.selectedDepartments.map((d) =>
        d.id === departmentId ? { ...d, canDraw: value } : d
      ),
    }));
  };

  const handleDepartmentErasePermissionChange = (departmentId: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      selectedDepartments: prev.selectedDepartments.map((d) =>
        d.id === departmentId ? { ...d, canErase: value } : d
      ),
    }));
  };

  const isFormValid = () => {
    if (formData.type === "USER") {
      return formData.selectedUsers.length > 0;
    } else {
      return formData.selectedDepartments.length > 0;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="lg" container={container}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Katılımcı Tipi</label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value="USER"
                checked={formData.type === "USER"}
                onChange={(e) => handleTypeChange(e.target.value as "USER")}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Kullanıcı</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value="DEPARTMENT"
                checked={formData.type === "DEPARTMENT"}
                onChange={(e) => handleTypeChange(e.target.value as "DEPARTMENT")}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Departman</span>
            </label>
          </div>
        </div>

        {formData.type === "USER" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Seçin</label>

            {formData.selectedUsers.length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-blue-900 mb-3">Seçili Kullanıcılar ({formData.selectedUsers.length})</div>
                <div className="space-y-3">
                  {formData.selectedUsers.map((item) => (
                    <div key={item.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {`${item.user.firstName || ""} ${item.user.lastName || ""}`.trim() || "İsimsiz"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUserRemove(item.id)}
                          className="text-red-500 hover:text-red-700 text-sm font-bold ml-2"
                        >
                          ×
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.canDraw}
                            onChange={(e) => handleUserPermissionChange(item.id, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Pen size={14} className="text-gray-600" />
                          <span className="text-sm text-gray-600">Çizim İzni</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.canErase}
                            onChange={(e) => handleUserErasePermissionChange(item.id, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Eraser size={14} className="text-gray-600" />
                          <span className="text-sm text-gray-600">Silme İzni</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <UserSelector
              value={currentSelectedUser}
              onChange={handleUserAdd}
              placeholder="Kullanıcı Seçin"
              excludeIds={[...formData.selectedUsers.map((u) => u.id), ...excludeUserIds]}
              allowClear={true}
              required={false}
              inline={true}
              autoOpen={true}
              hideSelected={true}
              className="text-sm"
            />
          </div>
        )}

        {formData.type === "DEPARTMENT" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Departman Seçin</label>

            {formData.selectedDepartments.length > 0 && (
              <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm font-medium text-green-900 mb-3">Seçili Departmanlar ({formData.selectedDepartments.length})</div>
                <div className="space-y-3">
                  {formData.selectedDepartments.map((item) => (
                    <div key={item.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-green-100">
                      <div className="flex items-center gap-2">
                        {item.department.color && (
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.department.color }} />
                        )}
                        <span className="text-sm font-medium text-gray-900">{item.department.name}</span>
                        <button
                          type="button"
                          onClick={() => handleDepartmentRemove(item.id)}
                          className="text-red-500 hover:text-red-700 text-sm font-bold ml-2"
                        >
                          ×
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.canDraw}
                            onChange={(e) => handleDepartmentPermissionChange(item.id, e.target.checked)}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <Pen size={14} className="text-gray-600" />
                          <span className="text-sm text-gray-600">Çizim İzni</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.canErase}
                            onChange={(e) => handleDepartmentErasePermissionChange(item.id, e.target.checked)}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <Eraser size={14} className="text-gray-600" />
                          <span className="text-sm text-gray-600">Silme İzni</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DepartmentSelector
              value={currentSelectedDepartment}
              onChange={handleDepartmentAdd}
              placeholder="Departman Seçin"
              excludeIds={[...formData.selectedDepartments.map((d) => d.id), ...excludeDepartmentIds]}
              allowClear={false}
              required={false}
              showColors={true}
              inline={true}
              className="text-sm"
            />
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 font-medium"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={!isFormValid() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
          >
            {isLoading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>}
            {isLoading ? "Paylaşılıyor..." : "Paylaş"}
          </button>
        </div>
      </form>
    </Modal>
  );
}